import { CoreImageView, CoreTextView } from '@core/widgets';
import { ConditionalRender, eventBus } from '@libs/core';
import { SystemEventTypeEnum } from '@libs/types';
import React, { useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { TextStyle } from '../../types/styles';
import { ToastBarType } from '../../types/toastBar.type';
import { getHref } from '../../widgets/Image/imageUtils';
import styles from './consumerToastBar.module.css';

const widgetUid = 'ToastBar';
const DEFAULT_DELAY = 2500;
const ICON_SIZE = 16;

const ConsumerToastBar = () => {
    const delay = useRef<number>(DEFAULT_DELAY);
    const [toastMessage, setToastMessage] = useState('');
    const [toastIconName, setToastIconName] = useState<string | undefined>(undefined);

    const close = () => {
        setToastMessage('');
        setToastIconName(undefined);
    };

    // Subscribe once: the bus replaces a subscription stored under the same uid without closing the previous one,
    // so re-subscribing on every message would leave orphaned handlers behind.
    useEffect(() => {
        eventBus.subscribe(SystemEventTypeEnum.TOAST_MESSAGE, widgetUid, (message) => {
            if (typeof message === 'string') {
                delay.current = DEFAULT_DELAY;
                setToastMessage(message as unknown as string);
                setToastIconName(undefined);
            } else if ((message as ToastBarType)?.message) {
                const messageObj = message as ToastBarType;
                setToastMessage(messageObj.message);
                setToastIconName(messageObj.iconName);
                delay.current = messageObj.delay || DEFAULT_DELAY;
            }
        });
        return () => {
            eventBus.unsubscribe(SystemEventTypeEnum.TOAST_MESSAGE, widgetUid);
        };
    }, []);

    // Auto-dismiss restarts for every new message; the cleanup also cancels the timer when the toast is closed manually.
    useEffect(() => {
        if (!toastMessage) {
            return;
        }
        const timerId = setTimeout(close, delay.current);
        return () => {
            clearTimeout(timerId);
        };
    }, [toastMessage]);

    const onSwipe = useSwipeable({
        onSwipedUp: () => close(),
        trackTouch: true,
    });

    if (!toastMessage) {
        return null;
    }

    return (
        <div data-test="toastBar" className={styles.toastBar}>
            <div className={styles.toastBarInner}>
                <div className={styles.toastBarContent} {...onSwipe} data-testid={widgetUid} onClick={close}>
                    <ConditionalRender condition={!!toastIconName}>
                        <span className={styles.toastBarIcon} data-test={`${widgetUid}_icon`}>
                            <CoreImageView
                                src={getHref(toastIconName)}
                                styleProps={{
                                    width: ICON_SIZE,
                                    height: ICON_SIZE,
                                    iconColor: 'var(--white, #FFFFFF)',
                                }}
                            />
                        </span>
                    </ConditionalRender>
                    <CoreTextView text={toastMessage} styleProps={{ ...TextStyle.secondary, main: 'var(--white)' }} />
                </div>
            </div>
        </div>
    );
};

export default ConsumerToastBar;
